import {
  type ForkTemplateSchema,
  forkTemplateSchema,
} from "@gearbox-protocol/deploy-tools-shared";
import { api } from "api";
import { DEFAULT_BACKEND_VERSION } from "components/fork-form";
import type { FileWithPath } from "react-dropzone";
import type { LoaderFunction } from "react-router-dom";
import { loadFiles } from "routes/fork/loadFiles";
import { prettifyError, ZodError } from "zod/v4";

export interface LoaderData {
  defaultValues: ForkTemplateSchema;
  defaultFiles?: FileWithPath[];
}

const loader: LoaderFunction = async ({ request }): Promise<LoaderData> => {
  const clone = new URL(request.url).searchParams.get("clone");
  if (clone === null) {
    return {
      defaultValues: {
        network: "Mainnet",
        description: "",
        timeout: "2h",
        backend: { version: DEFAULT_BACKEND_VERSION },
      },
    };
  }

  if (!clone) {
    throw new Error("failed to clone fork: fork to clone is not specified");
  }

  // never silently fall back to a blank form: the user asked to clone a
  // specific fork, and a blank form would drop both its options and its files
  try {
    const status = await api.status(clone);
    if (status.paused) {
      throw new Error(
        "cannot clone a paused fork: its files are not available",
      );
    }
    // to clean unused options
    const defaultValues = forkTemplateSchema.parse(status.options);
    const defaultFiles = await loadFiles(clone, status.files);
    return { defaultValues, defaultFiles };
  } catch (e) {
    let message = `${e}`;
    if (e instanceof ZodError) {
      message = prettifyError(e);
    } else if (e instanceof Error) {
      message = e.message;
    }
    throw new Error(`failed to clone fork ${clone}: ${message}`);
  }
};

export default loader;
